import { DateRangePicker, Notice, Select, Stack, Switch, TextArea, Truncate } from '@eleveo/components-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { toCalendarDateTime } from '@internationalized/date';
import clsx from 'clsx';
import React, { useMemo } from 'react';
import { useField } from 'react-final-form';
import { Trans, useTranslation } from 'react-i18next';

import { PTORequestFormValues } from './types';
import { max255chars, minDateTimeRange, required, startBeforeEnd } from './validations';
import { useAuth } from '../../app/AuthorizationProvider';
import { PTO_REQUEST_OPTIONS } from '../../config/agentsRequests';
import { TIME_FORMAT_12_HOURS, identifyTimeFormat } from '../../utils/time';
import { composeValidators } from '../../utils/validations/composeValidators';

export const CreatePTORequestModalBody: React.FC = () => {
  const { locale } = useAuth();
  const { t } = useTranslation();

  const requestTypesOptions = useMemo(
    () =>
      PTO_REQUEST_OPTIONS.map(({ icon, nameKey, subtype }) => {
        const optionName = t(nameKey);

        return {
          id: subtype,
          name: (
            <Stack spacing={10} className="items-center max-w-full">
              {icon}
              <Truncate>{optionName}</Truncate>
            </Stack>
          ),
          textValue: optionName,
        };
      }),
    [t]
  );

  const typeField = useField<PTORequestFormValues['type']>('type', {
    validate: required,
  });
  const useSpecificTimeField = useField<PTORequestFormValues['useSpecificTime']>('useSpecificTime');
  const rangeField = useField<PTORequestFormValues['range']>('range', {
    validate: composeValidators(
      required,
      startBeforeEnd({ fieldName: 'useSpecificTime', value: true }),
      minDateTimeRange({ minutes: 15 }, { fieldName: 'useSpecificTime', value: false })
    ),
  });
  const messageField = useField<PTORequestFormValues['message']>('message', {
    validate: max255chars,
  });

  const is12HourFormat = useMemo(() => identifyTimeFormat(locale) === TIME_FORMAT_12_HOURS, [locale]);
  const minRangeDifferenceLabel = `15 ${t('time.minute', { count: 15 })}`;

  return (
    <Stack className="py-10" direction="vertical" spacing={20}>
      <Stack spacing={15}>
        <Select
          errorMessage={t(typeField.meta.error)}
          isRequired
          label={t('schedule.request.absenceType')}
          onBlur={typeField.input.onBlur}
          onFocus={typeField.input.onFocus}
          onSelectionChange={(values) => typeField.input.onChange(values.values().next().value)}
          options={requestTypesOptions}
          selectedKeys={typeField.input.value ? [typeField.input.value] : []}
          selectionMode="single"
          testId="agent-portal:monthly-view:create-pto-request:type"
          validationState={typeField.meta.touched && typeField.meta.invalid ? 'invalid' : undefined}
          valueFormatter={(items) => items.at(0)?.value?.name}
        />
        <Stack direction="vertical">
          <DateRangePicker
            {...rangeField.input}
            className={clsx('time-off-request__range-picker', {
              'time-off-request__range-picker--12-hours-format': is12HourFormat,
            })}
            errorMessage={t(rangeField.meta.error, { difference: minRangeDifferenceLabel })}
            hideTimeZone
            isRequired
            granularity={useSpecificTimeField.input.value ? 'minute' : 'day'}
            label={t('date.range')}
            onBlur={rangeField.input.onBlur}
            onFocus={rangeField.input.onFocus}
            onChange={(newValue) =>
              rangeField.input.onChange(
                newValue && { start: toCalendarDateTime(newValue.start), end: toCalendarDateTime(newValue.end) }
              )
            }
            steps={{ minute: 15 }}
            testId="agent-portal:monthly-view:create-pto-request:range"
            validationState={rangeField.meta.touched && rangeField.meta.invalid ? 'invalid' : 'valid'}
            value={rangeField.input.value}
          />
          <Switch
            isSelected={useSpecificTimeField.input.value}
            onChange={useSpecificTimeField.input.onChange}
            testId="agent-portal:monthly-view:create-pto-request:range-type-switcher"
          >
            {t('date.setASpecificTime')}
          </Switch>
        </Stack>
      </Stack>
      <TextArea
        className="w-full"
        errorMessage={t(messageField.meta.error, { characters: 255 })}
        label={t('schedule.request.message')}
        onBlur={messageField.input.onBlur}
        onFocus={messageField.input.onFocus}
        onChange={messageField.input.onChange}
        testId="agent-portal:monthly-view:create-pto-request:message"
        value={messageField.input.value ?? ''}
        validationState={messageField.meta.touched && messageField.meta.invalid ? 'invalid' : undefined}
      />
      <Notice color="warning">
        <Trans key="schedule.request.newRequestNotice">
          Your <b>absence request must be accepted by your manager!</b>
          <br />
          (you&apos;ll be informed via e-mail as soon as possible)
        </Trans>
      </Notice>
    </Stack>
  );
};
